import { memo } from 'react'
import type { Primitive } from '@lib/dto/primitive.dto'
import { FurniturePreview } from '@/components/lazyDrawings'
import { CANVAS } from '@/floorplan/canvasColors'
import { cn } from '@/lib/utils'

/**
 * A furniture piece shown as Plan View shows furniture: its top-down drawing on
 * a plan-grid tile, so a list of pieces looks like the plan they go on. The
 * editor's inventory, the venue's furniture list and its inventory editor all
 * draw it the same way.
 *
 * A scrap of the canvas, so it takes the canvas's colors rather than the
 * theme's: the canvas stays light in dark mode, and a drawing made in its white
 * and navy vanishes on a dark tile. A piece with no drawing shows its
 * photograph, and one with neither just the grid.
 *
 * Memoized, because each drawing is its own small Konva stage and the lists it
 * sits in re-render often (the editor's on every change to the plan).
 */
export const FurnitureTile = memo(function FurnitureTile({
  primitives,
  imageUrl = null,
  size,
  fill = false,
  className,
}: {
  primitives: Primitive[]
  imageUrl?: string | null | undefined
  /**
   * The tile's side in pixels, with the drawing inset 4px — or, with `fill`,
   * the drawing's own size inside the parent's box.
   */
  size?: number
  /** Fill the parent instead — a thumbnail frame or a card's picture area, which set their own size. */
  fill?: boolean
  className?: string | undefined
}) {
  const side = size ?? 40
  function renderContent() {
    if (primitives.length > 0) {
      return <FurniturePreview primitives={primitives} size={fill ? (size ?? DRAWING_IN_FRAME) : side - 4} bare />
    }
    if (imageUrl) return <img src={imageUrl} alt="" className="h-full w-full object-cover" />
    return null
  }
  return (
    <span
      aria-hidden
      className={cn('grid shrink-0 place-items-center overflow-hidden rounded-sm', fill && 'h-full w-full', className)}
      style={{
        ...(fill ? {} : { width: side, height: side }),
        backgroundColor: CANVAS.sunken,
        backgroundImage: `radial-gradient(${CANVAS.dot} 1px, transparent 1.4px)`,
        backgroundSize: '8px 8px',
        backgroundPosition: '4px 4px',
      }}
    >
      {renderContent()}
    </span>
  )
})

/** The drawing's box inside a table's 56 × 42 thumbnail frame. */
const DRAWING_IN_FRAME = 38
